import {useNavigate} from "react-router-dom";
import clsx from "clsx";
import {
  HomeOutline,
  Add,
  MusicalNotesOutline,
  NewspaperOutline,
  CalendarOutline,
} from "@chayka/icons";
import {XStack, YStack} from "@chayka/ui";
import {useAuth} from "@/hooks/useAuth";

interface BottomTabsProps {
  variant?: "authorized" | "unauthorized";
}

const BOTTOM_TABS_PATHS = ["index", "schedule", "booking", "services", "news"] as const;

const NAVIGATION_BY_VARIANT: Record<
  NonNullable<BottomTabsProps["variant"]>,
  Record<(typeof BOTTOM_TABS_PATHS)[number], string>
> = {
  authorized: {
    index: "/app",
    schedule: "/app/my-bookings",
    booking: "/app/booking",
    services: "/app/services",
    news: "/app/news",
  },
  unauthorized: {
    index: "/guest",
    schedule: "/guest/schedule",
    booking: "/guest/booking",
    services: "/guest/services",
    news: "/guest/news",
  },
};

const IconWrapper: React.FC<React.PropsWithChildren> = ({children}) => (
  <YStack className="justify-center items-center gap-1">{children}</YStack>
);

export const BottomTabs: React.FC<BottomTabsProps> = ({variant = "unauthorized"}) => {
  const navigate = useNavigate();
  const {isAuthenticated, user} = useAuth();

  const ICONS_BY_PATH: Record<(typeof BOTTOM_TABS_PATHS)[number], React.ReactNode> = {
    index: (
      <IconWrapper>
        <HomeOutline size={20} />
        <span>Главная</span>
      </IconWrapper>
    ),
    schedule: (
      <IconWrapper>
        <CalendarOutline size={20} />
        {isAuthenticated && user?.confirmationStatus === "approved" ? (
          <span>Мои брони</span>
        ) : (
          <span>График</span>
        )}
      </IconWrapper>
    ),
    booking: (
      <IconWrapper>
        <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-background -mt-10">
          <Add size={30} />
        </div>
      </IconWrapper>
    ),
    services: (
      <IconWrapper>
        <MusicalNotesOutline size={20} />
        <span>Услуги</span>
      </IconWrapper>
    ),
    news: (
      <IconWrapper>
        <NewspaperOutline size={20} />
        <span>Новости</span>
      </IconWrapper>
    ),
  };

  return (
    <div className="app-bottom-tabs fixed bottom-0 left-0 right-0 bg-background px-4 pt-2 pb-4 flex justify-between shadow-[0px_-3px_30px_-3px_rgba(0,0,0,0.1)]">
      <XStack className="flex-1 max-w-160 mx-auto justify-between">
        {BOTTOM_TABS_PATHS.map((path) => (
          <button
            key={path}
            type="button"
            className={clsx(
              "text-xxs",
              NAVIGATION_BY_VARIANT[variant][path] === window.location.pathname
                ? "text-blackout"
                : path === "booking"
                  ? ""
                  : "text-secondary-gray opacity-70",
            )}
            onClick={() => navigate(NAVIGATION_BY_VARIANT[variant][path])}
            style={{width: "70px"}}
          >
            {ICONS_BY_PATH[path]}
          </button>
        ))}
      </XStack>
    </div>
  );
};
